import { useEffect, useRef, useState } from 'react';
import { intlTag } from '../../lib/format';
import { prefersReducedMotion } from '../../lib/motion';
import { benchmarkRatio, savingsScenario, DEFAULT_ILLUSTRATIVE_SPEND } from '../../lib/savings';
import { MEASURED_PATH } from '../../lib/measured';
import { landingFrontCopy } from '../../lib/strings/landing-front';
import { useLocale } from '../../state/useLocale';

/** The card's address: the bar's "Audited benchmarks" link points here. */
export const SAVINGS_ID = 'savings';

const REVEAL_MS = 1100;
const FOLLOW_MS = 240;

/**
 * A number that moves to its target instead of jumping, eased out. Reduced
 * motion, or no animation frame to draw on, and it is the target at once.
 */
function useTween(target: number, duration: number): number {
  const [shown, setShown] = useState(target);
  const from = useRef(target);
  useEffect(() => {
    if (prefersReducedMotion() || typeof requestAnimationFrame !== 'function') {
      from.current = target;
      setShown(target);
      return;
    }
    const start = performance.now();
    const origin = from.current;
    let frame = 0;
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const value = origin + (target - origin) * (1 - (1 - t) ** 3);
      from.current = value;
      setShown(value);
      if (t < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [target, duration]);
  return shown;
}

/**
 * The hero's savings card (owner, 2026-10-31: "just the slider ... this is
 * what you were paying and this is what you can pay"): one slider for a
 * monthly bill, two bars, and what is kept. When the card first comes into
 * view the "after" bar shrinks from the full bill to the benchmark's share
 * and the numbers count to it; after that they follow the slider.
 *
 * The figures are an illustration at the audited model-routing benchmark's
 * cost ratio (lib/savings.ts), and the one footnote says so. Each final value
 * also sits in `data-value`, which is what assistive tech's live region and
 * the tests read while the visible number is still moving.
 */
export function SavingsMeter() {
  const { locale } = useLocale();
  const s = landingFrontCopy[locale];
  const [spend, setSpend] = useState(DEFAULT_ILLUSTRATIVE_SPEND);
  const [revealed, setRevealed] = useState(false);
  const card = useRef<HTMLDivElement>(null);
  const ratio = benchmarkRatio('model');
  const scenario = savingsScenario(spend);

  useEffect(() => {
    const node = card.current;
    if (!node || typeof IntersectionObserver !== 'function' || prefersReducedMotion()) {
      setRevealed(true);
      return;
    }
    const seen = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setRevealed(true);
        seen.disconnect();
      }
    }, { threshold: 0.4 });
    seen.observe(node);
    return () => seen.disconnect();
  }, []);

  // The first move is the reveal, slow enough to watch; after it the numbers
  // follow the slider quickly.
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    if (!revealed) return;
    const t = window.setTimeout(() => setSettled(true), REVEAL_MS);
    return () => window.clearTimeout(t);
  }, [revealed]);
  const pace = settled ? FOLLOW_MS : REVEAL_MS;
  const after = useTween(revealed ? scenario.after : scenario.before, pace);
  const kept = useTween(revealed ? scenario.saved : 0, pace);
  const usd = (value: number) =>
    new Intl.NumberFormat(intlTag(locale), { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
  const width = (share: number) => (spend > 0 ? `${share * 100}%` : '0%');

  return (
    <div className="efficiency-example savings-meter" id={SAVINGS_ID} ref={card} role="region" aria-labelledby="savings-meter-title">
      <p className="label" id="savings-meter-title">{s.meterLabel}</p>
      <label className="savings-meter__spend" htmlFor="api-spend">
        <span>{s.spend}</span>
        <output htmlFor="api-spend">{usd(spend)}</output>
      </label>
      <input
        id="api-spend"
        type="range"
        min="0"
        max="10000"
        step="10"
        value={spend}
        aria-valuetext={usd(spend)}
        onChange={(event) => setSpend(Number(event.currentTarget.value))}
      />
      <div className="savings-meter__bars" aria-live="polite" aria-atomic="true">
        <div className="savings-meter__bar">
          <span>{s.today}</span>
          <strong data-value={usd(scenario.before)}>{usd(scenario.before)}</strong>
          <i style={{ width: width(1) }} />
        </div>
        <div className="savings-meter__bar is-after">
          <span>{s.after}</span>
          <strong data-value={usd(scenario.after)}>{usd(after)}</strong>
          <i style={{ width: width(revealed ? ratio : 1) }} />
        </div>
      </div>
      <p className="savings-meter__kept">
        <span>{s.keep}</span>
        <strong data-value={usd(scenario.saved)}>{usd(kept)}</strong>
        <span>{s.perMonth}</span>
      </p>
      <p className="note savings-meter__note">
        {s.footnote} <a href={MEASURED_PATH}>{s.how}</a>
      </p>
    </div>
  );
}

export default SavingsMeter;
